import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function Metal() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Metal Roofing",
          item: "https://www.saskroofingcompany.com/services/metal",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/metal#service",
      serviceType: "Metal Roofing Installation",
      name: "Metal Roofing",
      description:
        "Premium metal roofing installation in Saskatchewan. Standing seam and exposed fastener systems built for 50+ years of durability.",
      url: "https://www.saskroofingcompany.com/services/metal",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "15000",
        highPrice: "30000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Is a metal roof noisy when it rains or hails?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No, when installed over a solid roof deck and proper insulation, a metal roof is no noisier than a traditional asphalt shingle roof.",
          },
        },
        {
          "@type": "Question",
          name: "Does a metal roof attract lightning?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Metal roofs do not attract lightning. In fact, they are safer because they are non-combustible and won't catch fire if struck.",
          },
        },
        {
          "@type": "Question",
          name: "Can I walk on my metal roof?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, but you must know where to step (typically over the purlins or decking support) to avoid denting the panels, and it can be extremely slippery. We recommend leaving maintenance to professionals.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Metal Roofing Saskatchewan | Sask Roofing"
        description="Expert metal roofing contractor in Saskatchewan. We install durable standing seam and metal roofs that last 50+ years and easily shed heavy snow."
        schema={schema}
      />

      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
            alt="Metal Roofing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Metal Roofing Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Metal roofing is the ultimate long-term investment for your home.
              We install premium standing seam and exposed fastener metal roofs
              that effortlessly shed snow, resist hail, and last a lifetime.
            </p>
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Get a Free Metal Roof Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need Metal Roofing
              </h2>
              <p className="text-lg text-muted-foreground">
                You need a metal roof if you're looking for a permanent
                "one-and-done" roofing solution, want to increase your home's
                resale value, or live in a rural area where fire resistance and
                extreme weather durability are paramount.
              </p>
              <h3 className="text-2xl font-bold pt-4">How It Works</h3>
              <p className="text-lg text-muted-foreground">
                We custom-fit metal panels to your roof's exact dimensions. For
                standing seam systems, the fasteners are completely concealed
                under the seams, creating a sleek, watertight barrier that
                expands and contracts naturally with temperature changes.
              </p>
            </div>
            <div className="bg-muted/30 p-8 rounded-xl border">
              <h3 className="text-2xl font-bold mb-6">
                Why It Suits Saskatchewan
              </h3>
              <ul className="space-y-4">
                {[
                  "Easily sheds heavy winter snow loads, preventing roof collapse and ice dams.",
                  "Incredible resistance to large hail—often qualifying for insurance discounts.",
                  "Won't rot, crack, or warp during intense summer heat or -40°C deep freezes.",
                  "Expected lifespan of 50+ years with minimal maintenance.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-4xl">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">
              Materials We Use for Metal Roofing
            </h2>
            <p className="text-lg text-muted-foreground">
              We offer both premium standing seam and cost-effective exposed
              fastener metal roofing systems. For ultimate durability, we
              recommend our 24-gauge or 26-gauge steel panels featuring Kynar
              500® or SMP (Silicone Modified Polyester) paint finishes, which
              provide industry-leading protection against fading, chalking, and
              rusting. We install various profiles, including snap-lock and
              mechanical seam, ensuring a watertight fit that expands and
              contracts flawlessly through Saskatchewan's extreme temperature
              shifts.
            </p>
            <div className="mt-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
                alt="Standing seam metal roofing panel close-up"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Material Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Standing Seam vs. Exposed Fastener
          </h2>
          <div className="overflow-x-auto rounded-xl border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="p-4 font-bold border-b">Feature</th>
                  <th className="p-4 font-bold border-b text-primary">
                    Standing Seam (Concealed)
                  </th>
                  <th className="p-4 font-bold border-b">Exposed Fastener</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="p-4 font-medium">Upfront Cost</td>
                  <td className="p-4 text-muted-foreground">
                    $$$$ (Higher investment)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    $$ (More affordable)
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="p-4 font-medium">Weather Tightness</td>
                  <td className="p-4 text-muted-foreground">
                    Exceptional (No exposed holes)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Good (Requires washer maintenance)
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Thermal Movement</td>
                  <td className="p-4 text-muted-foreground">
                    Allows natural expansion/contraction
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Restricted (Can cause fastener backing)
                  </td>
                </tr>
                <tr className="bg-muted/30">
                  <td className="p-4 font-medium">Aesthetic</td>
                  <td className="p-4 text-muted-foreground">
                    Modern, sleek, premium look
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Traditional, agricultural/rustic look
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cost & Process */}
      <section className="py-20 bg-muted/50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>
              <div className="bg-white p-6 rounded-xl border border-primary/20 shadow-sm mb-6">
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
                  Estimated Investment
                </div>
                <div className="text-3xl font-bold text-foreground">
                  $8,000 - $30,000+
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  *Rough estimate for an average-sized Saskatchewan home. Final
                  cost depends on panel system.
                </p>
              </div>
              <p className="text-lg text-muted-foreground">
                Metal roofing requires a higher upfront investment but offers
                the lowest lifetime cost. In Saskatchewan, an exposed fastener
                metal roof typically ranges from $8,000 to $16,000, while a
                premium standing seam system ranges from $15,000 to $30,000+,
                depending on the square footage and complexity. For more
                details, see our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                .
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60"
                  alt="Completed Standing Seam Metal Roof"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Our Process</h2>
              <div className="space-y-6 mt-8">
                {[
                  {
                    title: "Consultation",
                    desc: "We discuss panel profiles, colors, and gauges.",
                  },
                  {
                    title: "Precise Measurement",
                    desc: "Metal is custom-ordered to minimize waste.",
                  },
                  {
                    title: "Preparation",
                    desc: "Installing specialized high-temp underlayment.",
                  },
                  {
                    title: "Installation",
                    desc: "Expert fitting of panels, ridge caps, and flashings.",
                  },
                  {
                    title: "Final Inspection",
                    desc: "Ensuring a perfect, watertight seal.",
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection service="Metal Roofing" serviceSlug="metal" />

      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Metal Roofing FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                Is a metal roof noisy when it rains or hails?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                No, when installed over a solid roof deck and proper insulation,
                a metal roof is no noisier than a traditional asphalt shingle
                roof.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Does a metal roof attract lightning?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Metal roofs do not attract lightning. In fact, they are safer
                because they are non-combustible and won't catch fire if struck.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can I walk on my metal roof?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, but you must know where to step (typically over the purlins
                or decking support) to avoid denting the panels, and it can be
                extremely slippery. We recommend leaving maintenance to
                professionals.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">
            Invest in a Roof That Lasts a Lifetime
          </h2>
          <p className="text-xl text-primary-foreground/80">
            We proudly serve Saskatoon, Regina, Moose Jaw, Prince Albert, Swift
            Current, Yorkton, Estevan, and Weyburn. Metal roofing projects
            usually take 2-4 days. Book your free inspection today to explore
            your options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Request a Free Metal Quote</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/process">See Our Process</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
